import { useState } from 'react';
import { RefreshCw, Copy, Check } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ApiConnectionsTab({ apiConnections, setToast, onReload }) {
  const [regenerating, setRegenerating] = useState(null);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [copied, setCopied] = useState(null);
  const [copyingFull, setCopyingFull] = useState(null);

  const handleRegenerate = async (keyId, keyName, merchantName) => {
    if (!window.confirm(`Regenerar la key "${keyName}" de ${merchantName}?\n\nLa key anterior sera revocada inmediatamente.`)) return;
    setRegenerating(keyId);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/super-admin/api-keys/${keyId}/regenerate`, null);
      setNewKeyResult(res.data.new_key);
      setToast({ type: 'success', message: `Key regenerada para ${merchantName}` });
      onReload();
    } catch (error) {
      setToast({ type: 'error', message: error.response?.data?.detail || 'Error al regenerar key' });
    } finally { setRegenerating(null); }
  };

  const handleCopyFull = async (userId, keyId) => {
    setCopyingFull(keyId);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/super-admin/merchants/${userId}/credentials`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const match = (res.data?.api_keys || []).find((k) => k.key_id === keyId);
      const fullKey = match?.full_key;
      if (!fullKey || fullKey.endsWith('...')) {
        setToast({ type: 'error', message: 'No se pudo recuperar la key completa. Regenerala para obtener un valor nuevo.' });
        return;
      }
      await navigator.clipboard.writeText(fullKey);
      setCopied(keyId);
      setTimeout(() => setCopied(null), 2000);
      setToast({ type: 'success', message: 'API Key completa copiada al portapapeles' });
    } catch (error) {
      setToast({ type: 'error', message: error.response?.data?.detail || 'Error al copiar la key' });
    } finally { setCopyingFull(null); }
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div data-testid="api-connections-tab">
      <h2 className="text-2xl font-bold mb-6" style={{ color: '#051C2C' }}>Conexiones API de Comercios</h2>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs text-gray-500">Total Comercios</p>
          <p className="text-2xl font-bold" style={{ color: '#003594' }}>{apiConnections.length}</p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs text-gray-500">API Keys Activas</p>
          <p className="text-2xl font-bold text-green-600">{apiConnections.reduce((sum, m) => sum + m.active_keys, 0)}</p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs text-gray-500">Total Keys</p>
          <p className="text-2xl font-bold text-gray-800">{apiConnections.reduce((sum, m) => sum + m.total_keys, 0)}</p>
        </div>
      </div>
      <div className="space-y-4">
        {apiConnections.map((merchant) => (
          <div key={merchant.user_id} className="bg-white border rounded-xl overflow-hidden">
            <div className="px-5 py-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-gray-800">{merchant.company_name}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${merchant.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{merchant.status}</span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{merchant.email} | {merchant.projects_count} proyecto(s)</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold" style={{ color: '#003594' }}>{merchant.active_keys}</p>
                <p className="text-xs text-gray-500">keys activas</p>
              </div>
            </div>
            {merchant.api_keys.length > 0 && (
              <div className="border-t bg-gray-50 px-5 py-3">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className="text-left py-1.5 font-medium text-gray-500">Nombre</th>
                      <th className="text-left py-1.5 font-medium text-gray-500">Key (prefijo)</th>
                      <th className="text-left py-1.5 font-medium text-gray-500">Proyecto</th>
                      <th className="text-left py-1.5 font-medium text-gray-500">Estado</th>
                      <th className="text-left py-1.5 font-medium text-gray-500">Ultimo uso</th>
                      <th className="text-right py-1.5 font-medium text-gray-500">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {merchant.api_keys.map((key) => (
                      <tr key={key.key_id} className="border-t border-gray-100">
                        <td className="py-2 font-medium text-gray-800">{key.key_name || '-'}</td>
                        <td className="py-2 font-mono text-gray-600">{key.key_prefix}</td>
                        <td className="py-2 text-gray-600">{key.project_name}</td>
                        <td className="py-2"><span className={`px-1.5 py-0.5 rounded text-xs ${key.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{key.status}</span></td>
                        <td className="py-2 text-gray-500">{key.last_used_at ? new Date(key.last_used_at).toLocaleDateString('es-VE') : 'Nunca'}</td>
                        <td className="py-2 text-right">
                          <div className="inline-flex items-center gap-1.5">
                            {key.status === 'active' && (
                              <button
                                data-testid={`copy-key-${key.key_id}`}
                                onClick={() => handleCopyFull(merchant.user_id, key.key_id)}
                                disabled={copyingFull === key.key_id}
                                title="Copiar API Key completa"
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 transition-colors"
                              >
                                {copied === key.key_id ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                                {copied === key.key_id ? 'Copiada' : (copyingFull === key.key_id ? '...' : 'Copiar')}
                              </button>
                            )}
                            {key.status === 'active' && (
                              <button
                                data-testid={`regenerate-key-${key.key_id}`}
                                onClick={() => handleRegenerate(key.key_id, key.key_name || key.key_prefix, merchant.company_name)}
                                disabled={regenerating === key.key_id}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 disabled:opacity-50 transition-colors"
                              >
                                <RefreshCw className={`w-3 h-3 ${regenerating === key.key_id ? 'animate-spin' : ''}`} />
                                {regenerating === key.key_id ? 'Regenerando...' : 'Regenerar'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {merchant.api_keys.length === 0 && (
              <div className="border-t bg-gray-50 px-5 py-3 text-center text-xs text-gray-400">Sin API Keys generadas</div>
            )}
          </div>
        ))}
        {apiConnections.length === 0 && (
          <div className="text-center py-12 text-gray-500">No hay comercios registrados</div>
        )}
      </div>

      {/* New Key Result Modal */}
      {newKeyResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setNewKeyResult(null)}>
          <div className="bg-white rounded-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b bg-green-600 rounded-t-xl">
              <h3 className="text-lg font-bold text-white">Key Regenerada Exitosamente</h3>
              <p className="text-sm text-green-100">Copia la nueva key — no se mostrara de nuevo</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Entorno</label>
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${newKeyResult.environment === 'test' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                  {newKeyResult.environment === 'test' ? 'QA / Test' : 'Produccion'}
                </span>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Nueva API Key</label>
                <div className="flex items-center gap-2 bg-gray-50 border rounded-lg p-3">
                  <code className="text-sm break-all flex-1 text-gray-800">{newKeyResult.full_key}</code>
                  <button
                    data-testid="copy-new-key"
                    onClick={() => handleCopy(newKeyResult.full_key, 'new-key')}
                    className="p-1.5 rounded hover:bg-gray-200 transition-colors flex-shrink-0"
                  >
                    {copied === 'new-key' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                  </button>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                La key anterior fue revocada. Actualiza la configuracion del comercio con la nueva key.
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end rounded-b-xl">
              <button onClick={() => setNewKeyResult(null)} className="px-4 py-2 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: '#003594' }}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
